import React from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { routes } from '@/app/routes';

// Helper to get breadcrumb label from route config
const getBreadcrumbLabel = (path: string): string => {
  // Try to find an exact match in the route config
  const route = routes.find(r => r.path === path);
  if (route && route.breadcrumb) return route.breadcrumb;
  // Fallback: prettify last segment
  return path.split('/').pop() || path;
};

// Function to generate breadcrumb items from a path
export const generateBreadcrumbs = (path: string) => {
  // Split the path into segments
  const segments = path.split('/').filter(Boolean);

  // Create breadcrumb items
  const breadcrumbs = segments.map((segment, index) => {
    // Build the path up to this segment
    const currentPath = `/${segments.slice(0, index + 1).join('/')}`;
    return {
      label: getBreadcrumbLabel(currentPath),
      path: currentPath,
      active: index === segments.length - 1
    };
  });

  // Add home as the first breadcrumb if we're not already on the home page
  if (segments.length > 0) {
    breadcrumbs.unshift({
      label: 'Home',
      path: '/',
      active: false
    });
  }

  return breadcrumbs;
};

// React hook to get breadcrumbs based on current location
export const useBreadcrumbs = () => {
  const location = useLocation();
  return generateBreadcrumbs(location.pathname);
};

// Breadcrumb component that automatically generates breadcrumbs based on current route
export const AutoBreadcrumb: React.FC = () => {
  const breadcrumbs = useBreadcrumbs();

  if (breadcrumbs.length <= 1) {
    return null; // Don't show breadcrumbs on the home page
  }

  return (
    <Breadcrumb className="text-sm md:text-base">
      <BreadcrumbList className="flex-wrap">
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={crumb.path || index}>
            <BreadcrumbItem>
              {crumb.active ? (
                <BreadcrumbPage className="text-xs md:text-sm font-medium">{crumb.label}</BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link to={crumb.path} className="text-xs md:text-sm hover:text-primary">
                    {crumb.label}
                  </Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export default AutoBreadcrumb;
